import {Flow} from 'flow-to-typescript-codemod';

import mapValues from 'lodash/mapValues';
import isFunction from 'lodash/isFunction';
import * as React from 'react';
import hoistNonReactStatics from 'hoist-non-react-statics';

import IndexStore from 'src/stores/index';
import {withRouter} from './withRouter';
import {FluxContext} from './root';
import pure from './pure';
import nameHoc from './nameHoc';
import useFluxStore from './useFluxStore';

// TODO (kyle): remove this.
const useFlux = useFluxStore;
export {useFlux};

const emptyObject = Object.freeze({});

type Params = {
  [key: string]: string;
};
type Mappers<C, M> = {
  [key: string]: (
    store: IndexStore,
    props: C & {
      params: Params;
    },
  ) => M;
};

type Mapper<C, M> = (
  props: C & {
    store: IndexStore;
    params: Params;
  },
) => M;

type Options = {
  pure?: boolean;
  storeKey?: string;
};

// TODO (kyle): pure: true
export const withFlux =
  <C extends unknown, M extends unknown>(
    mapper?: (
      arg1: C & {
        store: IndexStore;
      },
    ) => M,
    options: Options = Object.freeze({}),
  ): ((
    WrappedComponent: Flow.AbstractComponent<C>,
    // @ts-expect-error - TS2344 - Type 'C' does not satisfy the constraint 'M'.
  ) => Flow.AbstractComponent<Flow.Diff<C, M>>) =>
  <C extends unknown, M extends unknown>(
    WrappedComponent: Flow.AbstractComponent<C>,
    // @ts-expect-error - TS2344 - Type 'C' does not satisfy the constraint 'M'.
  ): Flow.AbstractComponent<Flow.Diff<C, M>> => {
    if (options.pure) {
      WrappedComponent = pure()(WrappedComponent);
    }

    const WrapperComponent = (props: {[key: string]: unknown}) => {
      const {store} = React.useContext(FluxContext);
      return (
        // @ts-expect-error - TS2769 - No overload matches this call.
        <WrappedComponent
          ref={props.fluxRef}
          // @ts-expect-error - TS2345 - Argument of type '((arg1: C & { store: IndexStore; }) => M) | undefined' is not assignable to parameter of type '((arg1: { store: never; }) => unknown) | undefined'.
          {...calcFluxProps(store, mapper, props, options.storeKey)}
        />
      );
    };

    // @ts-expect-error - TS2322 - Type 'ComponentType<unknown>' is not assignable to type 'AbstractComponent<Diff<C, M>, any>'.
    return nameHoc(
      // @ts-expect-error - TS2345 - Argument of type '((props: { [key: string]: unknown; }) => Element) & NonReactStatics<AbstractComponent<C, any>, {}>' is not assignable to parameter of type 'ComponentType<unknown>'.
      hoistNonReactStatics(WrapperComponent, WrappedComponent),
      WrappedComponent,
      'Flux',
    );
  };

function calcFluxProps(
  store: any,
  mapper:
    | undefined
    | ((
        arg1: unknown & {
          store: never;
        },
      ) => unknown),
  props: {
    [key: string]: unknown;
  },
  storeKey = 'store',
) {
  const router = props.router || null;
  let fluxProps = {...props, [storeKey]: store, router};

  if (mapper) {
    // @ts-expect-error - TS2345 - Argument of type '{ [x: string]: any; router: {} | null; }' is not assignable to parameter of type '{ store: never; }'.
    const mappedProps = mapper(fluxProps);
    // @ts-expect-error - TS2698 - Spread types may only be created from object types.
    fluxProps = {...fluxProps, ...mappedProps};
  }

  return fluxProps;
}

// NOTE (kyle): legacy HOCs
// FIXME(marcos): remove use of withRouter here #8433
const FluxComponent = <C extends unknown, M extends unknown>(
  WrappedComponent: Flow.AbstractComponent<C>,
  // @ts-expect-error - TS2322 - Type 'Readonly<{}>' is not assignable to type '(arg1: C & { store: IndexStore; params: { [key: string]: string; }; }) => M'.
  mappers: (
    arg1: C & {
      store: IndexStore;
      params: {
        [key: string]: string;
      };
    },
  ) => M = emptyObject,
  options: Options = emptyObject,
): Flow.AbstractComponent<
  // @ts-expect-error - TS2344 - Type 'C' does not satisfy the constraint 'M'.
  Flow.Diff<C, M> & {
    // @ts-expect-error - TS2314 - Generic type 'Ref' requires 1 type argument(s).
    fluxRef?: React.Ref;
  }
  // @ts-expect-error - TS2322 - Type 'ComponentType<Diff<PropsWithoutRef<Diff<C & { store: IndexStore; params: Params; }, M>> & RefAttributes<any>, { router: BaseHistory; location: RerouterLocation; params: Readonly<...>; }>>' is not assignable to type 'AbstractComponent<Diff<C, M> & { fluxRef?: any; }, any>'. | TS2345 - Argument of type 'Mapper<C, M> | ((props: any) => { [x: string]: M; })' is not assignable to parameter of type '((arg1: C & { store: IndexStore; params: Params; } & { store: IndexStore; }) => M) | undefined'. | TS2345 - Argument of type 'AbstractComponent<C, any>' is not assignable to parameter of type 'AbstractComponent<C & { store: IndexStore; params: Params; }, any>'.
> => withRouter(withFlux(massageMapper(mappers), options)(WrappedComponent));
export default FluxComponent;

function massageMapper<C, M>(mappers: Mappers<C, M> | Mapper<C, M>) {
  return isFunction(mappers)
    ? mappers
    : (props: any) =>
        mapValues(mappers, (mapper) => mapper(props.store, props));
}

export const fluxify =
  <C extends unknown, M extends unknown>(
    // @ts-expect-error - TS2322 - Type 'Readonly<{}>' is not assignable to type '(arg1: C & { store: IndexStore; params: { [key: string]: string; }; }) => M'.
    mappers: (
      arg1: C & {
        store: IndexStore;
        params: {
          [key: string]: string;
        };
      },
    ) => M = emptyObject,
    options: Options = emptyObject,
  ): ((WrappedComponent: Flow.AbstractComponent<C>) => Flow.AbstractComponent<
    // @ts-expect-error - TS2344 - Type 'C' does not satisfy the constraint 'M'.
    Flow.Diff<C, M> & {
      // @ts-expect-error - TS2314 - Generic type 'Ref' requires 1 type argument(s).
      fluxRef?: React.Ref;
    }
  >) =>
  <C extends unknown, M extends unknown>(
    WrappedComponent: Flow.AbstractComponent<C>,
  ): Flow.AbstractComponent<
    // @ts-expect-error - TS2344 - Type 'C' does not satisfy the constraint 'M'.
    Flow.Diff<C, M> & {
      // @ts-expect-error - TS2314 - Generic type 'Ref' requires 1 type argument(s).
      fluxRef?: React.Ref;
    }
  > =>
    // @ts-expect-error - TS2719 - Type 'import("/Users/diwaker/SpacedOut/ui-server/src/types/flow-to-typescript-codemod").Flow.AbstractComponent<import("/Users/diwaker/SpacedOut/ui-server/src/types/flow-to-typescript-codemod").Flow.Diff<C, M> & { fluxRef?: any; }, any>' is not assignable to type 'import("/Users/diwaker/SpacedOut/ui-server/src/types/flow-to-typescript-codemod").Flow.AbstractComponent<import("/Users/diwaker/SpacedOut/ui-server/src/types/flow-to-typescript-codemod").Flow.Diff<C, M> & { fluxRef?: any; }, any>'. Two different types with this name exist, but they are unrelated. | TS2345 - Argument of type 'import("/Users/diwaker/SpacedOut/ui-server/src/types/flow-to-typescript-codemod").Flow.AbstractComponent<C, any>' is not assignable to parameter of type 'import("/Users/diwaker/SpacedOut/ui-server/src/types/flow-to-typescript-codemod").Flow.AbstractComponent<C, any>'.
    FluxComponent(WrappedComponent, mappers, options);
